// vue-i18n 实例 + 语言切换。
//
// 语言清单按版本（edition.ui.locales）：国内只有简体中文；海外为全量
// （与 Android ComAppLanguageType 对齐）。后端没有专门「设置语言」接口，
// 靠每次请求的 Language 头同步内容（见 src/api/client.ts）。
//
// 初始语言：用户手动选择(localStorage) > 浏览器探测(命中本版支持) > edition.ui.defaultLocale。
// 首次探测结果会落盘，让 api client 的 Language 头取到同一个值（前后端语言一致）。
//
// 文案：目前仅 zh / en 有完整 UI 文案（en 用 typeof zh 约束，漏翻译编译报错）；
// 繁体暂用简体文案，其余语言 UI chrome 暂回退英文——服务端内容已由 Language 头本地化。
// 接入真正翻译时，把下面 messages 里对应语言从别名换成独立文案文件即可。

import { createI18n } from "vue-i18n";
import { Locale } from "vant";
import vantZhCN from "vant/es/locale/lang/zh-CN";
import vantZhTW from "vant/es/locale/lang/zh-TW";
import vantEnUS from "vant/es/locale/lang/en-US";
import vantThTH from "vant/es/locale/lang/th-TH";
import vantViVN from "vant/es/locale/lang/vi-VN";
import vantIdID from "vant/es/locale/lang/id-ID";
import vantHiIN from "vant/es/locale/lang/hi-IN";
import vantBnBD from "vant/es/locale/lang/bn-BD";
import { edition } from "@edition";
import zh from "./locales/zh";
import en from "./locales/en";
import th from "./locales/th";
import ms from "./locales/ms";
import vi from "./locales/vi";
import id from "./locales/id";
import hi from "./locales/hi";
import tl from "./locales/tl";
import bn from "./locales/bn";
import zhTW from "./locales/zh-TW";
import { LOCALES, type AppLocale } from "./locales-meta";

export type { AppLocale } from "./locales-meta";

const STORAGE_KEY = "jzbro_locale";

/** 本版支持的语言（顺序 = 展示顺序），来自 edition 配置。 */
export const AVAILABLE_LOCALES: ReadonlyArray<{
  value: AppLocale;
  label: string;
}> = edition.ui.locales.map((code) => ({
  value: code,
  label: LOCALES[code].label,
}));

function isSupported(code: string): code is AppLocale {
  return (edition.ui.locales as readonly string[]).includes(code);
}

/** 浏览器语言 → 本版支持的某个 locale；命中不了返回 null。 */
function detectFromBrowser(): AppLocale | null {
  const raw = (navigator.language || "").toLowerCase();
  // 中文：TW/HK/MO → 繁体，其余 → 简体（与 Android mapLocaleToSupported 对齐）
  if (raw.startsWith("zh")) {
    const tw = raw.includes("tw") || raw.includes("hk") || raw.includes("mo");
    if (tw && isSupported("zh-TW")) return "zh-TW";
    if (isSupported("zh")) return "zh";
  }
  // 印尼语系统可能给 in（旧码）；菲律宾语可能给 fil
  const base = raw.split("-")[0];
  const aliases: Record<string, AppLocale> = { in: "id", fil: "tl" };
  const code = aliases[base] ?? base;
  return isSupported(code) ? code : null;
}

function resolveInitial(): AppLocale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && isSupported(saved)) return saved;
  } catch {
    /* 隐私模式 / 非浏览器环境 localStorage 不可用 */
  }
  const detected = detectFromBrowser();
  const initial = detected ?? (edition.ui.defaultLocale as AppLocale);
  // 落盘首次探测结果，供 api client 的 Language 头读取（保持前后端语言一致）。
  try {
    localStorage.setItem(STORAGE_KEY, initial);
  } catch {
    /* ignore */
  }
  return initial;
}

const initial = resolveInitial();

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: initial,
  fallbackLocale: "en",
  messages: {
    zh,
    en,
    "zh-TW": zhTW,
    th,
    ms,
    vi,
    id,
    hi,
    tl,
    bn,
  },
});

// locale code → Vant 内置语言包（无对应包的语言回退 en-US）。
const VANT_PACKS: Partial<Record<AppLocale, { name: string; pack: object }>> = {
  zh: { name: "zh-CN", pack: vantZhCN },
  "zh-TW": { name: "zh-TW", pack: vantZhTW },
  en: { name: "en-US", pack: vantEnUS },
  th: { name: "th-TH", pack: vantThTH },
  vi: { name: "vi-VN", pack: vantViVN },
  id: { name: "id-ID", pack: vantIdID },
  hi: { name: "hi-IN", pack: vantHiIN },
  bn: { name: "bn-BD", pack: vantBnBD },
};

function applySideEffects(locale: AppLocale): void {
  const v = VANT_PACKS[locale] ?? { name: "en-US", pack: vantEnUS };
  Locale.use(v.name, v.pack);
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }
}

/** 切换语言：更新 vue-i18n + 持久化 + 同步 Vant 与 <html lang>。 */
export function setLocale(locale: AppLocale): void {
  i18n.global.locale.value = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* ignore */
  }
  applySideEffects(locale);
}

/** 当前语言。 */
export function getLocale(): AppLocale {
  return i18n.global.locale.value as AppLocale;
}

// 初始化即同步一次 Vant 语言 + <html lang>
applySideEffects(initial);
